import React, { useCallback, useEffect, useState } from 'react';
import Form from '../../../../../components/forms/form';
import '../style.scss';
import { Button, Tooltip, Tabs } from '@syneto/compass-react';
import Utils from '../../../../../helpers/Utils';
import { organizeUnitsByType } from '../../../../../helpers/UnitsOfMeasurement';
import {
	applyTierChange,
	buildEmptyTier,
	buildUnlimitedTier,
	describeSyncScope,
	displayTierValue,
	EDITIONS_SYNC_SCOPE,
	hasUnlimitedTier,
	isUnlimitedTier
} from '../licenseRules';
import { endingVersionOptions } from '../versionRules';

export const ModalBody = (props) => {
	const { versions, uploadedImage, setUploadedImage, setExistingImageUrl, change, editedAddon, getUnitsOfMeasurement, unitsOfMeasurement } = props;
	const [tiers, setTiers] = useState(editedAddon?.addonTiers ? editedAddon.addonTiers : []);
	const [versionOptions, setVersionOptions] = useState([]);
	const [startingVersion, setStartingVersion] = useState('');
	const [endingVersion, setEndingVersion] = useState(null);
	const [tabsActiveKey, setTabsActiveKey] = useState('en');
	const [isEditMode, setIsEditMode] = useState(false);
	
	useEffect(() => {
		setIsEditMode(!!editedAddon);
	}, [editedAddon]);

	const startingVersionChanged = (e) => {
		setStartingVersion(e);
	};

	/*
	 * The Ending options follow the Starting version: the released versions from it on and the caps that reach it
	 * (6.0.x, 7.x.x), plus the value the license already has (SYN-3461). The field also takes a typed value, which the
	 * validation checks.
	 */
	const endingOptions = endingVersionOptions(versions, startingVersion || editedAddon?.minProductVersion, endingVersion ?? editedAddon?.maxProductVersion);

	const addTier = () => {
		setTiers([...tiers, buildEmptyTier(Utils.uuidv4())]);
	};

	const addUnlimitedTier = () => {
		setTiers([...tiers, buildUnlimitedTier(Utils.uuidv4())]);
	};

	const handleTierChange = (index, e) => {
		let value, name;

		// Handle React Select component (passes option object directly)
		if (e && e?.value !== undefined) {
			value = e.value;
			// Extract field name from the form field name
			const fieldName = e.name || '';
			name = fieldName.match(/\.(.*)/) ? fieldName.match(/\.(.*)/)[1] : fieldName;
		} else if (e?.target) { 	// Handle regular form inputs (synthetic event)
			value = e.target.value;
			name = e.target.name.match(/\.(.*)/)[1];
		} else {
			return; // Invalid event
		}

		setTiers(applyTierChange(tiers, index, name, value, unitsOfMeasurement));
	};

	const removeTier = (index) => {
		const newTiers = tiers.filter((_, i) => { return i !== index; });
		setTiers(newTiers);
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		const reader = new FileReader();

		reader.onloadend = () => {
			setUploadedImage(reader.result);
		};

		if (file) {
			reader.readAsDataURL(file);
		}
	};

	const handleRemoveImage = () => {
		setUploadedImage('');
		setExistingImageUrl('');
	};

	useEffect(() => {
		if (editedAddon && editedAddon.addonTiers) {
			setTiers(editedAddon.addonTiers);
		} else {
			setTiers([]);
		}
	}, [editedAddon]);

	useEffect(() => {
		setVersionOptions(renderVersionsOptions(versions));
	}, [versions]);

	useEffect(() => {
		change('addonTiers', tiers);
	}, [tiers, change]);

	// Fetch units of measurement when component mounts
	useEffect(() => {
		getUnitsOfMeasurement();
	}, [getUnitsOfMeasurement]);

	const maxPeriodYearsOptions = [
		{ value: 1, label: '1 year' },
		{ value: 2, label: '2 years' },
		{ value: 3, label: '3 years' },
		{ value: 4, label: '4 years' },
		{ value: 5, label: '5 years' },
		{ value: 6, label: '6 years' },
		{ value: 7, label: '7 years' },
		{ value: 8, label: '8 years' },
		{ value: 9, label: '9 years' },
		{ value: 10, label: '10 years' },
		{ value: 99, label: '99 years' }
	];

	const maxTrialDurationDaysOptions = [
		{ value: 0, label: 'None' },
		{ value: 30, label: '30 days' },
		{ value: 60, label: '60 days' },
		{ value: 90, label: '90 days' },
		{ value: 120, label: '120 days' },
		{ value: 150, label: '150 days' },
		{ value: 180, label: '180 days' }
	];

	const renderVersionsOptions = (versions) => {
		return versions ? versions.map((version) => {
			return { value: version, label: version };
		}) : [{ value: '4', label: '4' }];
	};
	
	if (!unitsOfMeasurement.length) {
		return null;
	}

	return (
		<fieldset className="border-0">
			<Tabs
				activeKey={tabsActiveKey}
				onSelect={(tabsActiveKey) => { return setTabsActiveKey(tabsActiveKey); }}
			>
				<Tabs.Tab eventKey="en" title="English">
					<div className="row">
						<Form.text name="name" label="License name" placeholder="Name" required autocomplete="off" />
					</div>
					<div className="row">
						<Form.text name="summary" label="Summary" placeholder="Short description" required autocomplete="off" />
					</div>
					<div className="row">
						<div className="col-8">
							<Form.textarea
								name="description"
								label="Description"
								placeholder="Long description"
								style={{ height: '120px' }}
								required
							/>
						</div>
						<div className="col-4">
							<label className="form-label">Thumbnail</label>
							{!uploadedImage && (
								<>
									<label className="img-upload-btn btn-link" htmlFor="fileUpload"><i className="fas fa-upload"></i><span>Upload image</span></label>
									<input
										type="file"
										id="fileUpload"
										name="fileUpload"
										accept="image/*"
										onChange={handleFileUpload}
									/>
								</>
							)}
							{uploadedImage && (
								<div className="image-container">
									<img src={uploadedImage} alt="Uploaded" />
									<Button className="remove-image-btn" type="button" appearance="danger" role="tertiary" onClick={handleRemoveImage}>
								Remove image
									</Button>
								</div>
							)}
						</div>
					</div>
				</Tabs.Tab>
				<Tabs.Tab eventKey="it" title="Italian">
					<div className="row">
						<Form.text name="translations.it.name" label="License name" placeholder="Name" required autocomplete="off" />
					</div>
					<div className="row">
						<Form.text name="translations.it.summary" label="Summary" placeholder="Short description" required autocomplete="off" />
					</div>
					<div className="row">
						<div className="col-8">
							<Form.textarea
								name="translations.it.description"
								label="Description"
								placeholder="Long description"
								style={{ height: '120px' }}
								required
							/>
						</div>
						<div className="col-4">
							<label className="form-label">Thumbnail</label>
							{!uploadedImage && (
								<>
									<label className="img-upload-btn btn-link" htmlFor="fileUpload"><i className="fas fa-upload"></i><span>Upload image</span></label>
									<input
										type="file"
										id="fileUpload"
										name="fileUpload"
										accept="image/*"
										onChange={handleFileUpload}
									/>
								</>
							)}
							{uploadedImage && (
								<div className="image-container">
									<img src={uploadedImage} alt="Uploaded" />
									<Button className="remove-image-btn" type="button" appearance="danger" role="tertiary" onClick={handleRemoveImage}>
								Remove image
									</Button>
								</div>
							)}
						</div>
					</div>
				</Tabs.Tab>
				<Tabs.Tab eventKey="es" title="Spanish">
					<div className="row">
						<Form.text name="translations.es.name" label="License name" placeholder="Name" required autocomplete="off" />
					</div>
					<div className="row">
						<Form.text name="translations.es.summary" label="Summary" placeholder="Short description" required autocomplete="off" />
					</div>
					<div className="row">
						<div className="col-8">
							<Form.textarea
								name="translations.es.description"
								label="Description"
								placeholder="Long description"
								style={{ height: '120px' }}
								required
							/>
						</div>
						<div className="col-4">
							<label className="form-label">Thumbnail</label>
							{!uploadedImage && (
								<>
									<label className="img-upload-btn btn-link" htmlFor="fileUpload"><i className="fas fa-upload"></i><span>Upload image</span></label>
									<input
										type="file"
										id="fileUpload"
										name="fileUpload"
										accept="image/*"
										onChange={handleFileUpload}
									/>
								</>
							)}
							{uploadedImage && (
								<div className="image-container">
									<img src={uploadedImage} alt="Uploaded" />
									<Button className="remove-image-btn" type="button" appearance="danger" role="tertiary" onClick={handleRemoveImage}>
								Remove image
									</Button>
								</div>
							)}
						</div>
					</div>
				</Tabs.Tab>
			</Tabs>
			<hr />
			<div className="row">
				<Form.text
					name="presentationVideoUrl"
					label="Youtube link"
					placeholder="https://www.youtube.com/watch?v=T2lp3kaBkcQ"
					autocomplete="off"
				/>
			</div>
			<div className="row">
				<div className="col-3">
					<Form.select
						label="License period"
						name="maxPeriodYears"
						options={maxPeriodYearsOptions}
						emptyOption="Please select"
						required
					/>
				</div>
				<div className="col-3">
					<Form.select
						label="Trial duration"
						name="maxTrialDurationDays"
						options={maxTrialDurationDaysOptions}
						emptyOption="Please select"
						required
					/>
				</div>
			</div>
			<h4 className="mt-4">Tiers</h4>
			{tiers.length ? (
				<div>
					{tiers.map((tier, index) => {
						const value = displayTierValue(tier, unitsOfMeasurement);
						const unlimited = isUnlimitedTier(tier);

						return (
							<div key={tier.uuid ?? tier.id} className="row">
								<div className="col-3">
									<Form.text
										name={`addonTiers[${index}].name`}
										label="Tier name"
										placeholder="Tier name"
										onChange={(e) => { return handleTierChange(index, e); }}
										autocomplete="off"
										required
									/>
								</div>
								<div className="col-3">
									<Form.text
										name={`addonTiers[${index}].code`}
										label="Tier code"
										placeholder="Tier code"
										onChange={(e) => { return handleTierChange(index, e); }}
										autocomplete="off"
										required
									/>
								</div>
								{unlimited ? (
									<div className="col-5">
										<label className="form-label">Tier value</label>
										<p className="form-control-plaintext tier-unlimited-value">Unlimited, no unit</p>
									</div>
								) : (
									<>
										<div className="col-2">
											<Form.text
												type="number"
												min="0"
												step="any"
												label="Tier value"
												placeholder="Tier value"
												input={{ name: `addonTiers[${index}].value`, defaultValue: value, onChange: (e) => { return handleTierChange(index, e); } }}
												autocomplete="off"
												required
											/>
										</div>
										<div className="col-3">
											<Form.select
												name={`addonTiers[${index}].unitOfMeasurementId`}
												label="Unit of measurement"
												options={organizeUnitsByType(unitsOfMeasurement)}
												emptyOption="Select unit"
												onChange={(value) => { return handleTierChange(index, { value, name: `addonTiers[${index}].unitOfMeasurementId` }); }}
											/>
										</div>
									</>
								)}
								<div className="delete-tier-btn col-1 d-flex align-items-center">
									<Button type="button" appearance="danger" role="tertiary" onClick={() => { return removeTier(index); }}>
										Delete
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<h6 className="no-tiers-defined">No tiers defined</h6>
			)}
			<Button className="m-0 mb-3" type="button" role="tertiary" onClick={addTier}>
				Add new tier
			</Button>
			<Button className="mb-3" type="button" role="tertiary" onClick={addUnlimitedTier} disabled={hasUnlimitedTier(tiers)}>
				Add Unlimited tier
			</Button>

			<div className="row">
				<div className="col-3">
					<div className="d-flex mb-1">
						<label className="form-label">Feature ID (code name)</label>
						{isEditMode ? (
							<Tooltip text="This field is used by the production machines running this license. It should not be changed. If you need to change it, contact the development team." placement="right" trigger="hover">
								<i className="fas fa-info-circle ms-2"></i>
							</Tooltip>
						) : (
							<Tooltip text="The development team should supply the information for this field. Please pay special attention when introducing it as it cannot be modified again from Operations." placement="right" trigger="hover">
								<i className="fas fa-info-circle ms-2"></i>
							</Tooltip>
						)}
					</div>
					<Form.text name="code" placeholder="Code name" autocomplete="off" required disabled={isEditMode}/>
				</div>
				<div className="col-5">
					<div className="d-flex mb-1">
						<label className="form-label">Machines that get this license</label>
						<Tooltip text="Set by Central from the license itself and not editable here. Licenses for machines on 6.1 or later and the licenses Central ships with cannot be deleted." placement="right" trigger="hover">
							<i className="fas fa-info-circle ms-2"></i>
						</Tooltip>
					</div>
					<p className="form-control-plaintext license-sync-scope">{describeSyncScope(isEditMode ? editedAddon?.syncScope : EDITIONS_SYNC_SCOPE)}</p>
				</div>
			</div>

			<h4 className="mt-4">
				Supported product versions
				<Tooltip text="Supporting product versions should start with the first version that includes the license and end with the last version that includes the license. The ending version can also be a whole release: 6.0.x means up to the last 6.0 release, 7.x.x up to the last 7 release." placement="top" trigger="hover">
					<i className="fas fa-info-circle"></i>
				</Tooltip>
			</h4>
			<div className="row">
				<div className="col-3">
					<Form.select
						label="Starting"
						name="minProductVersion"
						options={versionOptions}
						emptyOption="Please select"
						onChange={startingVersionChanged}
						required
					/>
				</div>
				<div className="col-3">
					<Form.comboSelect
						label="Ending"
						name="maxProductVersion"
						options={endingOptions}
						emptyOption="None"
						onValueChange={setEndingVersion}
					/>
				</div>
			</div>
		</fieldset>
	);
};

export default ModalBody;
